import { useEffect, useMemo } from 'react'
import { AreaChart } from '@/components/charts/AreaChart'
import { BarChart } from '@/components/charts/BarChart'
import { StatCard } from '@/components/charts/StatCard'
import { AppShell } from '@/components/layout/AppShell'
import { useAuth } from '@/hooks/use-auth'
import { formatDateTime } from '@/lib/utils'
import { useDashboardStore } from '@/stores/dashboard-store'

export function Dashboard() {
  useAuth()
  const { stats, charts, isLoading, fetchStats, fetchCharts } = useDashboardStore()

  useEffect(() => {
    fetchStats()
    fetchCharts()
  }, [fetchStats, fetchCharts])

  const creditChartData = useMemo(() => {
    if (!charts?.credits) return []
    const byDate: Record<string, Record<string, string | number>> = {}
    for (const row of charts.credits) {
      if (!byDate[row.date]) byDate[row.date] = { date: row.date }
      byDate[row.date][row.actionType] = Number(row.total)
    }
    return Object.values(byDate)
  }, [charts])

  const pendingWaitlist = (stats?.waitlist?.pending ?? 0) + (stats?.waitlist?.unconfirmed ?? 0)

  return (
    <AppShell title="Dashboard">
      {isLoading && !stats ? (
        <div className="flex items-center justify-center py-24">
          <p className="font-mono text-xs text-ink-faint animate-pulse">Loading dashboard...</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Total Users" value={stats?.totalUsers ?? 0} subtitle="All time" />
            <StatCard
              label="Active Users"
              value={stats?.activeUsers7d ?? 0}
              subtitle="Last 7 days"
            />
            <StatCard label="Beta Signups" value={pendingWaitlist} subtitle="Pending review" />
            <StatCard
              label="Credits Used"
              value={stats?.creditsUsed30d ?? 0}
              subtitle="Last 30 days"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <AreaChart
              title="User Registrations"
              data={charts?.registrations ?? []}
              dataKey="count"
              color="#C4714A"
            />
            <BarChart
              title="Credit Usage by Type"
              data={creditChartData}
              bars={[
                { dataKey: 'fashion_identify', color: '#C4714A', label: 'Fashion ID' },
                { dataKey: 'tryon_generation', color: '#6B8F71', label: 'Try-On' },
                { dataKey: 'photo_enhancement', color: '#C4A14A', label: 'Enhancement' },
              ]}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <AreaChart
              title="API Requests"
              data={charts?.requests ?? []}
              dataKey="count"
              color="#6B8F71"
            />

            <div className="rounded-lg border border-dashed border-border bg-surface p-5">
              <p className="mb-4 font-mono text-[11px] tracking-wider text-ink-faint uppercase">
                Recent Signups
              </p>
              {stats?.recentSignups && stats.recentSignups.length > 0 ? (
                <div className="space-y-2.5">
                  {stats.recentSignups.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between border-b border-dashed border-border-muted pb-2.5 last:border-0 last:pb-0"
                    >
                      <div>
                        <p className="font-mono text-xs text-ink">{user.name}</p>
                        <p className="font-mono text-[10px] text-ink-faint">{user.email}</p>
                      </div>
                      <p className="font-mono text-[10px] text-ink-faint">
                        {formatDateTime(user.createdAt)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="py-8 text-center font-mono text-xs text-ink-faint">
                  No recent signups
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </AppShell>
  )
}
